/* eslint-disable no-nested-ternary */
/* eslint-disable complexity */
/* global window */

import React from 'react';

import { RegionIdEnum } from '@search/filter-enums/enums/Region';
import { DealType } from '@search/filter-enums/enums';
import { useRouter, OfferRoute } from '@search/offer-search-routes/src';
import { yMapVectorRound } from '@search/ymap/src/Vector';
import { useGqlContext } from '@search/gql-client/src/gqlContext';
import { useGql2Loader } from '@search/gql-client/src/useGql2';
import { useClassifiedOfferDataLayerPush } from '../../../analytics/ClassifiedOfferGtmContext';
import { OfferYMapUnionPlacemarkItem } from '../../union/UnionPlacemarkItem';
import { useOfferYMapUnionItemUrlBuilder } from '../../union/useItemUrlBuilder';
import {
    OfferNearestMapQuery$data as OfferNearestMapQueryResponse, OfferNearestMapQuery$variables as OfferNearestMapQueryVariables
} from './__generated__/OfferNearestMapQuery.graphql';
import { OfferNearestMapQuery } from './OfferNearestMapQuery';
import { pointCompat } from './useSearchOffersMapQuery';

function useSearchOffersMapFetcher() {
    const gql = useGqlContext();

    return React.useCallback(
        (vars: OfferNearestMapQueryVariables, opts?: { signal?: AbortSignal }) => gql.client<
            OfferNearestMapQueryVariables,
            OfferNearestMapQueryResponse
        >(OfferNearestMapQuery, vars, opts)
            .then(response => {
                return response;
            }),
        [ gql ]
    );
}

export function useOfferNearestMapLoader() {
    return useGql2Loader(useSearchOffersMapFetcher(), {
        cachePrefix: 'OfferNearestMapQuery'
    });
}

const defaultDelta = { x: 0.06, y: 0.02 };

export function useOfferNearestMapState({
    offerNearestMapLoad,
    offerId,
    center,
    saveInRoute = false,
    delta = defaultDelta,
    multiOfferUrlParams,
    dealType
}: {
    saveInRoute?: boolean;
    delta?: {x: number; y: number};
    center?: {
        readonly latitude: number;
        readonly longitude: number;
    };
    offerNearestMapLoad: ReturnType<typeof useOfferNearestMapLoader>;
    offerId: string;
    multiOfferUrlParams?: NonNullable<Parameters<typeof useOfferYMapUnionItemUrlBuilder>[0]>['multiOfferUrlParams'];
    region: RegionIdEnum;
    dealType: DealType;
}) {
    const route = useRouter().route(OfferRoute);
    const getItemUrl = useOfferYMapUnionItemUrlBuilder({
        multiOfferUrlParams
    });

    const isOpened = route.params().mapBounds?.lx !== undefined;
    const centerNormalized = center ? yMapVectorRound({ x: center.longitude, y: center.latitude }) : undefined;
    const initialBounds: OfferNearestMapQueryVariables['filters']['bounds'] | undefined = ! centerNormalized ?
        undefined :
        {
            lx: centerNormalized.x - delta.x,
            ly: centerNormalized.y - delta.y,
            rx: centerNormalized.x + delta.x,
            ry: centerNormalized.y + delta.y
        };

    const [ bounds, setBoundsRaw ] = React.useState(initialBounds);

    const openMap = React.useCallback(() => {
        if (isOpened) return;
        setBoundsRaw(initialBounds);
        if (saveInRoute) {
            route.push({
                ...route.params(),
                mapBounds: initialBounds
            });
        }
    }, [ isOpened, initialBounds?.lx, initialBounds?.ly, initialBounds?.rx, initialBounds?.ry ]);

    const setBounds = React.useCallback((
        next?: {
            min: {x: number; y: number};
            max: {x: number; y: number};
        }
    ) => {
        if (! next) {
            setBoundsRaw(initialBounds);
            if (saveInRoute) {
                window.history.back();
                // route.replace({
                //     ...route.params(),
                //     mapBounds: undefined
                // });
            }

            return;
        }

        const nextBounds = {
            lx: next.min.x,
            ly: next.min.y,
            rx: next.max.x,
            ry: next.max.y
        };

        setBoundsRaw(nextBounds);
        if (saveInRoute) {
            route.replace({
                ...route.params(),
                mapBounds: nextBounds
            });
        }
    }, [ route, saveInRoute, initialBounds?.lx, initialBounds?.ly, initialBounds?.rx, initialBounds?.ry ]);

    const response = typeof window !== 'undefined' && bounds ? offerNearestMapLoad({
        filters: {
            offerId,
            bounds
        }
    }) : undefined;

    const dataRef = React.useRef(response?.data?.searchOffersMapByOffer);

    if (response?.data?.searchOffersMapByOffer) {
        dataRef.current = response?.data?.searchOffersMapByOffer;
    }

    const points = dataRef.current?.points;
    const dataLayerPush = useClassifiedOfferDataLayerPush();
    const responseItems = React.useMemo(() => points?.map(raw => {
        const item = pointCompat(raw);

        const event = getOfferPlacemarkGtmId(raw, dealType);

        const next: OfferYMapUnionPlacemarkItem = {
            ...item,
            isActive: false,
            onClick: (e, element) => {
                element?.dispatchEvent(new MouseEvent('click'));
                event && dataLayerPush({ event });
            },
            url: getItemUrl(item)
        };

        return next;
    }), [ points, getItemUrl ]);

    const mapBounds = React.useMemo(() => ! bounds ? undefined : {
        min: {
            x: bounds.lx,
            y: bounds.ly
        },
        max: {
            x: bounds.rx,
            y: bounds.ry
        }
    }, [ bounds ]);

    return {
        setBounds,
        tileUrl: dataRef.current?.tileUrl,
        bounds: mapBounds,
        responseItems,
        openMap,
        isOpened,
        isLoading: response?.loading
    };
}

function getOfferPlacemarkGtmId(
    nearestPoint: OfferNearestMapQueryResponse['searchOffersMapByOffer']['points'][number],
    dealType: DealType
): string | undefined {
    let dataLayerEvent = '';

    if (
        nearestPoint.__typename === 'SingleOfferPointFlat' ||
        nearestPoint.__typename === 'SingleOfferPointHouse' ||
        nearestPoint.__typename === 'SingleOfferPointLand' ||
        nearestPoint.__typename === 'SingleOfferPointRoom'
    ) {
        const offerPoint = nearestPoint.offer;

        // вторичка
        dataLayerEvent = offerPoint.dealType?.__typename === 'ProfOfferDealSell' ?
            'classified_map_click_open_second_offer' :
            'classified_map_click_rent_open_second_offer';

        if (
            offerPoint.realtyObject?.__typename === 'ProfOfferFlat' &&
            offerPoint.dealType?.__typename === 'ProfOfferDealSell' &&
            offerPoint.realtyObject.building?.isNewbuilding
        ) {
            dataLayerEvent = 'classified_map_click_open_new_offer';
        }

        // VAS приоритетнее новостройки, но не новостройки от застройщика
        if (offerPoint.status?.hasAnyPriority) {
            dataLayerEvent = 'classified_map_click_open_second_vas_offer';
        }

        if (
            offerPoint.realtyObject?.__typename === 'ProfOfferFlat' &&
            offerPoint.dealType?.__typename === 'ProfOfferDealSell' &&
            offerPoint.seller?.sellerType === 'DEVELOPER' &&
            offerPoint.realtyObject.building?.isNewbuilding
        ) {
            dataLayerEvent = 'classified_map_click_open_new_dev_offer';
        }
    }
    if (
        nearestPoint.__typename === 'MultiOfferPointFlat' ||
        nearestPoint.__typename === 'MultiOfferPointHouse' ||
        nearestPoint.__typename === 'MultiOfferPointLand' ||
        nearestPoint.__typename === 'MultiOfferPointRoom'
    ) {
        dataLayerEvent = dealType === DealType.SELL ?
            'classified_map_click_sale_open_multdom_offer' :
            'classified_map_click_rent_open_multdom_offer';
    }
    return dataLayerEvent;
}
